/**
 * The header search.
 *
 * Deliberately narrow in scope: "do we have this in stock" is the lookup a shop does twenty
 * times a day, so that's what this answers, at `/stock/lookup` — not the Variants catalogue it
 * used to route to, which had no quantity figure a caller could trust (nullable, never
 * shop-scoped). A box labelled "search everything" that only really searches one thing is worse
 * than a box that admits what it does.
 *
 * Absent entirely for a user without `stock.view` — the same gate-by-omission rule as the rail.
 * Gated on `stock.view`, not `product.view`, now that this is a stock question: `/stock/lookup`
 * needs `stock.view` and nothing more (see `StockQuantityResponse`'s own doc comment for why it
 * needs no `cost.view`), so the search box and its destination agree about who may use either.
 */

import { useState, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TextField from '@mui/material/TextField';

import { Permission, can, selectUser } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';

export function GlobalSearch() {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const [term, setTerm] = useState('');

  if (!can(user, Permission.StockView)) return null;

  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = term.trim();
    if (trimmed.length === 0) return;

    void navigate(`/stock/lookup?search=${encodeURIComponent(trimmed)}`);
  };

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <TextField
        data-tour="search"
        size="small"
        value={term}
        placeholder="Is this in stock? Search by name or SKU…"
        aria-label="Search stock"
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        sx={{
          width: { xs: '100%', sm: 320, lg: 380 },
          '& .MuiOutlinedInput-root': {
            bgcolor: 'var(--pos-surface-base)',
            borderRadius: 'var(--pos-radius-chip)',
          },
          // The header is chrome; a hard outline here reads as a form field on every screen.
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pos-border-subtle)' },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          },
        }}
      />
    </form>
  );
}
